"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useCallback, useMemo, createContext, useContext, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import Script from "next/script";
import { Table } from "@/types";

/** Geo data extracted from Cloudflare headers (server-side) */
export interface GeoData {
  country: string | null;
  city: string | null;
  region: string | null;
  continent: string | null;
  ip: string | null;
  timeZone: string | null;
}
import { useUpdateUserProfile } from "@/utils/react-query-hooks";
import { UserContext } from "../AppProviders";
import type { AnnualUpsellOffer } from "@/utils/annual-upsell";
import AppSidebar from "@/components/presentational/tailwind/AppSidebar";
import { MobileNavProvider } from "@/contexts/MobileNavContext";
import { AccountSwitcherProvider, useAccountSwitcher } from "@/contexts/AccountSwitcherContext";
import { useGetAllAccountsForUser } from "@/hooks/queries/accounts";
import { proxyImageUrl } from "@/lib/image-proxy";

// Lazy load MobileNavDrawer for better initial load performance (mobile only)
const MobileNavDrawer = dynamic(() => import("@/components/presentational/tailwind/MobileNavDrawer"), {
  ssr: false
});
import { UpgradeHeader } from "./components/UpgradeHeader";
import DemoModeHeader, { DEMO_HEADER_HEIGHT } from "./components/DemoModeHeader";
import AppAnchor from "@/components/AppAnchor/AppAnchor";
// Note: Saans font is already loaded by root layout via Saans.variable
// Do NOT import here to avoid duplicate font loading (~850KB wasted)
import { MotionProvider } from "@/components/ui/motion-provider";
import { IntercomProvider, IntercomFacade } from "@/components/intercom";
import PostHogLoader from "@/components/analytics/PostHogLoader";
import { FiArrowLeft } from "react-icons/fi";
import { HiOutlineLogout } from "react-icons/hi";

// Outfame tracking pixel IDs (same as RouterEventWrapper for marketing pages)
const META_PIXEL_ID = "753687921113071";
const TIKTOK_PIXEL_ID = "D5UVE1JC77U5QAK8T8OG";

// Dev debug components
import DevDebugButton from "@/components/ui/DevDebugButton";
import DebugBentoModal from "@/components/presentational/tailwind/DebugBentoModal";

// ============================================
// AVATAR PRELOADER
// Preloads account avatar images early using <link rel="preload">
// This starts loading before SidebarAccountSelector mounts, eliminating flash
// ============================================
function AvatarPreloader() {
  const $ = _c(8);
  const {
    data: accounts
  } = useGetAllAccountsForUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [preloadUrls, setPreloadUrls] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== accounts) {
    t1 = () => {
      if (!accounts || accounts.length === 0) {
        return;
      }
      const urls = accounts.filter(_temp).map(_temp2).filter(Boolean);
      setPreloadUrls(urls);
    };
    t2 = [accounts];
    $[1] = accounts;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (preloadUrls.length === 0) {
    return null;
  }
  let t3;
  if ($[4] !== preloadUrls) {
    t3 = preloadUrls.map(_temp3);
    $[4] = preloadUrls;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <>{t3}</>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

// Keyboard shortcut handler for account switcher (Cmd+K / Ctrl+K)
// Must be inside AccountSwitcherProvider to access context
// Only works on desktop - mobile has no keyboard shortcuts
function _temp3(url) {
  return <link key={url} rel="preload" as="image" href={url} fetchPriority="high" />;
}
function _temp2(a_0) {
  return proxyImageUrl(a_0.avatar_url);
}
function _temp(a) {
  return a.avatar_url;
}
function AccountSwitcherShortcut() {
  const $ = _c(4);
  const {
    open,
    isOpen
  } = useAccountSwitcher();
  let t0;
  let t1;
  if ($[0] !== isOpen || $[1] !== open) {
    t0 = () => {
      const handleKeyDown = e => {
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
          e.preventDefault();
          const isDesktop = window.matchMedia("(min-width: 768px)").matches;
          if (!isDesktop) {
            return;
          }
          if (!isOpen) {
            open();
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t1 = [open, isOpen];
    $[0] = isOpen;
    $[1] = open;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}

// Lazy load promotional, tracking, and optional UI components for better performance
const TikTokPromoCard = dynamic(() => import("./components/TikTokPromoCard"), {
  ssr: false
});
const DemoBanner = dynamic(() => import("./components/DemoBanner"), {
  ssr: false
});
const PasskeyPrompt = dynamic(() => import("./components/PasskeyPrompt"), {
  ssr: false
});
const TrackingScripts = dynamic(() => import("./components/TrackingScripts"), {
  ssr: false
});
const PaymentFailedBanner = dynamic(() => import("./components/PaymentFailedBanner"), {
  ssr: false
});
const WeakFundingBanner = dynamic(() => import("./components/WeakFundingBanner"), {
  ssr: false
});
const AnnualUpsellBanner = dynamic(() => import("@/components/billing/AnnualUpsellBanner"), {
  ssr: false
});

// User profile context
export const UserProfileContext = createContext(null);
export function ClientLayout({
  children,
  hasUpgradableAccounts,
  userProfile: initialUserProfile,
  email,
  geoData,
  paymentFailedAccount,
  weakFundingAccount,
  annualUpsellOffer
}: {
  children: ReactNode;
  hasUpgradableAccounts: boolean;
  userProfile: Table<"user_profiles">;
  email: string;
  geoData: GeoData;
  paymentFailedAccount?: {
    id: string;
    username: string | null;
  } | null;
  weakFundingAccount?: {
    id: string;
    fundingType: "prepaid" | "debit";
  } | null;
  annualUpsellOffer?: AnnualUpsellOffer | null;
}) {
  const router = useRouter();
  const pathName = usePathname();

  // Disable browser scroll restoration — prevents mobile Safari from retaining scroll position
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Scroll to top on every client-side page navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathName]);

  // Debug modal state
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);

  // Derive page states from pathname - no useState, no re-renders on navigation
  // This is a major performance win: removes 4 useState + useEffect that caused re-renders
  const {
    isOnAccountsPage,
    isOnAdminPage,
    isOnMaxPage,
    isOnSetupPage
  } = useMemo(() => ({
    isOnAccountsPage: pathName === "/app/accounts",
    isOnAdminPage: pathName?.includes("/app/app_admin") ?? false,
    isOnMaxPage: pathName?.includes("/max") ?? false,
    isOnSetupPage: pathName?.includes("/setup") ?? false
  }), [pathName]);
  const searchParams = useSearchParams();
  const setSearchParam = useCallback((key: string, value: string) => {
    if (typeof window === "undefined") return;
    if (key === "step" && value === "0") {
      router.push(`/app/setup`, {
        scroll: false
      });
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set(key, value);
    router.push(`?${params.toString()}`, {
      scroll: false
    });
  }, [router]);
  const user = useContext(UserContext);
  const {
    mutate: updateUserProfile
  } = useUpdateUserProfile({});

  // Memoize the update callback for TrackingScripts to prevent re-renders
  const handleUpdateProfile = useCallback((data: Record<string, unknown>) => {
    updateUserProfile(data, {
      onError: error => console.error("Error updating profile:", error)
    });
  }, [updateUserProfile]);

  // Check if we're on demo subdomain for bottom padding
  const [isDemoMode, setIsDemoMode] = useState(false);
  useEffect(() => {
    setIsDemoMode(typeof window !== "undefined" && window.location.hostname.split(".")[0] === "demo");
  }, []);

  // Set data attribute for demo-specific CSS scoping (e.g., Intercom position offset)
  useEffect(() => {
    if (isDemoMode) {
      document.documentElement.setAttribute("data-demo", "true");
    } else {
      document.documentElement.removeAttribute("data-demo");
    }
    return () => document.documentElement.removeAttribute("data-demo");
  }, [isDemoMode]);

  // PostHog identity for signed-in users (never the demo visitor). Fire-and-forget; the helper is
  // imported dynamically so it stays out of the dashboard's initial bundle.
  useEffect(() => {
    if (isDemoMode || !user?.id || window.location.hostname.split(".")[0] === "demo") return;
    const traits = {
      email: user.email,
      name: initialUserProfile?.full_name || user.user_metadata?.full_name
    };
    import("@/lib/analytics/posthog-client").then(m => m.phIdentify(user.id, traits)).catch(() => {});
  }, [isDemoMode, user, initialUserProfile]);

  // Deferred prefetch for setup page - only in demo mode
  // Runs after LCP using requestIdleCallback for zero performance impact
  useEffect(() => {
    if (!isDemoMode) return;
    const prefetchSetup = () => {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = "https://www.outfame.com/setup";
      link.as = "document";
      document.head.appendChild(link);
    };
    if ("requestIdleCallback" in window) {
      requestIdleCallback(prefetchSetup, {
        timeout: 2000
      });
    } else {
      // Safari fallback
      setTimeout(prefetchSetup, 1000);
    }
  }, [isDemoMode]);

  // Apply body styles for dashboard layout (prevents scroll, sets height)
  // Using useEffect instead of styled-jsx to avoid React 19 hydration mismatch
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    // Store original styles
    const originalHtmlOverflow = html.style.overflow;
    const originalHtmlHeight = html.style.height;
    const originalBodyOverflow = body.style.overflow;
    const originalBodyHeight = body.style.height;

    // Apply dashboard styles
    html.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    // Cleanup on unmount
    return () => {
      html.style.overflow = originalHtmlOverflow;
      html.style.height = originalHtmlHeight;
      body.style.overflow = originalBodyOverflow;
      body.style.height = originalBodyHeight;
    };
  }, []);

  // Tracking scripts (Intercom, GA, Meta) are now handled by the TrackingScripts component
  // which defers loading until browser idle time for better TTI

  // removed old TikTok Pixel code - now handled by RouterEventWrapper

  return <>
            {/* PostHog: loads after window load + idle; skips demo and /app/app_admin (posthog-client.ts) */}
            <PostHogLoader />

            <UserProfileContext.Provider value={initialUserProfile}>
                <MobileNavProvider>
                    <AccountSwitcherProvider>
                        {/* Cmd+K shortcut for account switcher - only on dashboard (not setup) */}
                        {!isOnSetupPage && <AccountSwitcherShortcut />}
                        {/* Preload account avatars early via <link rel="preload"> - faster than JS Image() */}
                        <AvatarPreloader />
                        <MotionProvider>
                        {/* Intercom Provider - lazy loads with user identification */}
                        {/* In demo mode, don't pass user identity so each visitor gets anonymous session */}
                        <IntercomProvider user={user && !isDemoMode ? {
              id: user.id,
              email: user.email || undefined,
              name: initialUserProfile?.full_name || user.user_metadata?.full_name || undefined,
              createdAt: user.created_at,
              avatarUrl: initialUserProfile?.avatar_url || undefined
            } : undefined} loadOnIdle={true} idleTimeout={5000}>

                        {/* Debugging */}
                        {/* {user && account && userContext && (
                         <>
                         <p>initialUserProfile: {JSON.stringify(initialUserProfile)}</p>
                         <p>User: {JSON.stringify(user)}</p>
                         <p>Account: {JSON.stringify(account)}</p>
                         <p>UserContext: {JSON.stringify(userContext)}</p>
                         </>
                         )} */}

                        {/* Font class handled by Tailwind config using --font-dashboard CSS variable from root layout */}

                        {/* Setup page uses different layout (no sidebar) */}
                        {isOnSetupPage ? <div className="relative block min-h-screen bg-white font-dashboard">
                                <header className="z-11 sticky inset-x-0 top-0 z-10 border-b-1 border-gray-200 bg-white/80 py-3 backdrop-blur-lg lg:py-4" style={{
                  backdropFilter: "blur(16px)"
                }}>
                                    {/* Setup page header */}
                                    <div className="mx-auto px-4 sm:px-6 lg:px-8">
                                        <div className="flex items-center">
                                            <div className="flex w-full justify-between">
                                                {searchParams.get("step") ? <Link key={searchParams.get("step")} title="Back" className="group flex items-center gap-2 font-dashboard" href="#" onClick={e => {
                          e.preventDefault();
                          setSearchParam("step", (parseInt(searchParams.get("step")) - 1).toString());
                        }}>
                                                        <FiArrowLeft className="h-5 w-5 text-gray-500 group-hover:text-gray-700" />
                                                        <span className="text-gray-500 group-hover:text-gray-700">Back</span>
                                                    </Link> : <div className="w-[63px]"></div>}
                                                <AppAnchor title="Outfame" className="group flex items-center gap-2" href="/app/setup">
                                                    <Image width={120} height={24} className="h-6 w-auto" src="/img/outfame-logo-full.svg" priority={true} alt="Outfame" />
                                                </AppAnchor>
                                                {/* Plain <a>: a next/link here prefetched the logout API route (GET /api/logout?_rsc=… 404) */}
                                                <a href="/api/logout" title="Log out" className="group flex w-[83px] items-center gap-2 font-dashboard">
                                                    <HiOutlineLogout className="h-5 w-5 text-gray-500 group-hover:text-gray-700" />
                                                    <span className="text-gray-500 group-hover:text-gray-700">Log out</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </header>
                                {children}
                            </div> : <>
                                {/* Fixed header - Demo mode header OR Upgrade header */}
                                {isDemoMode ? <div className="fixed left-0 right-0 top-0 z-20">
                                        <DemoModeHeader />
                                    </div> : hasUpgradableAccounts && !isOnAdminPage ? <div className="fixed left-0 right-0 top-0 z-20">
                                        <UpgradeHeader />
                                    </div> : null}

                                {/* Fixed dashboard container - fills remaining viewport, only content scrolls */}
                                <div className={`fixed bottom-0 left-0 right-0 top-0 z-10 flex flex-col bg-ink pb-2 pt-0 font-dashboard md:pt-2 ${isDemoMode || hasUpgradableAccounts && !isOnAdminPage ? "top-[40px] md:top-[44px]" : ""}`}>
                                    {/* Unified dashboard wrapper - fills remaining space, rounded on all corners */}
                                    <div className="flex flex-1 overflow-hidden rounded-b-2xl bg-dashbg md:rounded-2xl">
                                        {/* Desktop Sidebar - hidden on admin pages */}
                                        {!isOnAdminPage && <div className="hidden shrink-0 md:block">
                                                <AppSidebar userProfile={initialUserProfile} hasUpgradableAccounts={hasUpgradableAccounts} />
                                            </div>}

                                        {/* Main Content Area - scrollable within fixed container */}
                                        <main className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain">
                                            {/* Payment-failed recovery banner (demo gate 2/3: never in demo) */}
                                            {!isDemoMode && !isOnAdminPage && paymentFailedAccount && <PaymentFailedBanner username={paymentFailedAccount.username} />}
                                            {/* Weak-funding card-upgrade prompt (demo gate 2/3: never in demo).
                                                PaymentFailedBanner takes precedence — never both. */}
                                            {!isDemoMode && !isOnAdminPage && !paymentFailedAccount && weakFundingAccount && <WeakFundingBanner fundingType={weakFundingAccount.fundingType} />}
                                            {/* Annual upsell — lowest banner priority: only when NO payment
                                                problem is showing (payment-failed / weak-funding always win). */}
                                            {!isDemoMode && !isOnAdminPage && !paymentFailedAccount && !weakFundingAccount && annualUpsellOffer && <AnnualUpsellBanner offer={annualUpsellOffer} />}
                                            <div className={`flex-1 ${isDemoMode ? "pb-20" : ""}`}>{children}</div>

                                            {/* Demo banner - lazy loaded */}
                                            <DemoBanner />
                                        </main>
                                    </div>
                                </div>
                            </>}

                        {/* Mobile Nav Drawer - premium bottom sheet for mobile navigation */}
                        {!isOnSetupPage && <MobileNavDrawer hasUpgradableAccounts={hasUpgradableAccounts} />}

                        {/* TikTok promo card - lazy loaded */}
                        <TikTokPromoCard />

                        {/* Deferred tracking scripts - loads after browser idle for better TTI */}
                        {user && <TrackingScripts user={user} userProfile={initialUserProfile} onUpdateProfile={handleUpdateProfile} geoData={geoData} />}

                        {/* Dev debug tools */}
                        <DevDebugButton onClick={() => setIsDebugModalOpen(true)} />
                        <DebugBentoModal isOpen={isDebugModalOpen} onClose={() => setIsDebugModalOpen(false)} />

                        {/* Intercom facade button - shows until real Intercom loads */}
                        <IntercomFacade />
                        </IntercomProvider>
                        </MotionProvider>
                    </AccountSwitcherProvider>
                </MobileNavProvider>
            </UserProfileContext.Provider>

            {/* Meta Pixel + TikTok Pixel initialization for dashboard routes */}
            {/* These pixels are loaded separately from RouterEventWrapper (marketing pages) */}
            {/* because /app uses a different layout tree that doesn't include RouterEventWrapper */}
            {process.env.NODE_ENV === "production" && <>
                    {/* Meta Pixel */}
                    <Script id="meta-pixel-init" strategy="afterInteractive">
                        {`
                            !function(f,b,e,v,n,t,s)
                            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                            n.queue=[];t=b.createElement(e);t.async=!0;
                            t.src=v;s=b.getElementsByTagName(e)[0];
                            s.parentNode.insertBefore(t,s)}(window, document,'script',
                            'https://connect.facebook.net/en_US/fbevents.js');
                            fbq('init', '${META_PIXEL_ID}');
                            fbq('track', 'PageView');
                        `}
                    </Script>
                    <noscript>
                        <img height="1" width="1" style={{
          display: "none"
        }} src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`} alt="" />
                    </noscript>

                    {/* TikTok Pixel */}
                    <Script id="tiktok-pixel-init" strategy="afterInteractive">
                        {`
                            !function (w, d, t) {
                                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq._q=ttq._q||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t._q.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var a=d.createElement("script");a.type="text/javascript",a.async=!0,a.src=r+"?sdkid="+e+"&lib="+t;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(a,s)};
                                ttq.load('${TIKTOK_PIXEL_ID}');
                                ttq.page();
                            }(window, document, 'ttq');
                        `}
                    </Script>

                    {/* Returning visitor identify — fast path before TrackingScripts */}
                    <Script id="returning-visitor-identify" strategy="afterInteractive">
                        {`
                            (function() {
                                try {
                                    var m = document.cookie.match(/(?:^|; )_of_em=([^;]*)/);
                                    var u = document.cookie.match(/(?:^|; )_of_uid=([^;]*)/);
                                    var email = m ? decodeURIComponent(m[1]) : null;
                                    var uid = u ? decodeURIComponent(u[1]) : null;
                                    if (!email) return;
                                    // TikTok identify
                                    if (window.ttq && typeof window.ttq.identify === 'function') {
                                        var d = { email: email };
                                        if (uid) d.external_id = uid;
                                        window.ttq.identify(d);
                                    }
                                    // Meta identify
                                    if (window.fbq) {
                                        window.fbq('init', '${META_PIXEL_ID}', { em: email });
                                    }
                                } catch(e) {}
                            })();
                        `}
                    </Script>
                </>}
        </>;
}